import { settingApi } from '@oneview/api/settingApi';
import type {
    ModelEditFormComponentValues,
    ModelEditFormValues,
} from '@oneview/components/~advisor/Models/ManageModels/ModelEditForm';
import { createQueryFn } from '@oneview/redux/utils';
import {
    Account,
    ModelComponent,
    OrderFilterType,
    OverExposedType,
    PositionType,
    TopPositionsType,
} from '@orbis-systems/orbis-api-middleware';
import { divide, multiply, showToast } from '@orbis-systems/orbis-react-toolkit';
import { api } from './baseApi';
import { DataTags } from './dataTags';

export const modelApi = api.injectEndpoints({
    endpoints: (build) => ({
        updateModelComponents: build.query({
            queryFn: createQueryFn(
                async (
                    {
                        model,
                        newComponents,
                        oldComponents,
                    }: {
                        model: ModelEditFormValues;
                        newComponents: ModelEditFormComponentValues[];
                        oldComponents: ModelEditFormComponentValues[];
                    },
                    { extra: api, dispatch },
                ) => {
                    const removedComponents = oldComponents
                        .filter((oldItem) => newComponents.every((newItem) => newItem.symbol !== oldItem.symbol))
                        .map((item) => ({ ...item, weight: 0 }));

                    const addedComponents = newComponents.filter((newItem) => {
                        const oldItem = oldComponents.find((oldItem) => oldItem.symbol === newItem.symbol);
                        return !oldItem || newItem.weight !== oldItem.weight;
                    });

                    const components = [...addedComponents, ...removedComponents];

                    await Promise.all([
                        components.map((item) =>
                            api.orbisSdk.post('v2/advisory/model/component/update', {
                                symbol: item.symbol,
                                percentage: divide(item.weight, 100),
                                option: false,
                                type: 'SECURITY',
                                model: {
                                    id: model.id,
                                },
                            }),
                        ),
                        model.id
                            ? dispatch(
                                  settingApi.endpoints.saveSettings.initiate({
                                      newSettings: {
                                          model: {
                                              [model.id]: newComponents.reduce<{
                                                  [key: string]: number;
                                              }>(
                                                  (result, item) => {
                                                      if (item.threshold > 0) {
                                                          result[item.symbol] = item.threshold;
                                                      }
                                                      return result;
                                                  },
                                                  {
                                                      defaultRebalanceThreshold: model.defaultRebalanceThreshold,
                                                  },
                                              ),
                                          },
                                      },
                                  }),
                              )
                            : Promise.resolve(),
                    ]);
                },
            ),
        }),
        getModelPortfolio: build.query({
            queryFn: createQueryFn(
                (
                    {
                        modelId,
                        combined = true,
                        symbol,
                        positionType,
                        currency,
                    }: {
                        modelId: number;
                        currency?: string;
                        combined?: boolean;
                        symbol?: string;
                        positionType?: PositionType;
                    },
                    { extra: api },
                ) => api.orbisMiddleware.model.getPortfolio(modelId, { combined, currency, symbol, positionType }),
            ),
        }),
        getModelAccountTypesBreakdown: build.query({
            queryFn: createQueryFn((modelId: number, { extra: api }) =>
                api.orbisMiddleware.stat.getModelAccountTypesBreakdownStat(modelId),
            ),
        }),
        getModelMarketCapBreakdown: build.query({
            queryFn: createQueryFn((modelId: number, { extra: api }) =>
                api.orbisMiddleware.stat.getModelMarketCapBreakdownStat(modelId),
            ),
        }),
        getModelSectorsBreakdown: build.query({
            queryFn: createQueryFn((modelId: number, { extra: api }) =>
                api.orbisMiddleware.stat.getModelSectorsBreakdownStat(modelId),
            ),
        }),
        getModelAccountSizesBreakdown: build.query({
            queryFn: createQueryFn((modelId: number, { extra: api }) =>
                api.orbisMiddleware.stat.getModelAccountSizesBreakdownStat(modelId),
            ),
        }),
        getModelTopPositionsBreakdown: build.query({
            queryFn: createQueryFn(
                (
                    { modelId, by = TopPositionsType.Weight }: { modelId: number; by?: TopPositionsType },
                    { extra: api },
                ) => api.orbisMiddleware.stat.getModelTopPositionsStat(modelId, by),
            ),
        }),
        getModelOverExposedAccounts: build.query({
            queryFn: createQueryFn(
                (
                    { modelId, threshold = 0, type }: { modelId: number; threshold: number; type: OverExposedType },
                    { extra: api },
                ) => api.orbisMiddleware.model.getOverExposedAccounts(modelId, threshold, type),
            ),
        }),
        getModel: build.query({
            queryFn: createQueryFn((modelId: number, { extra: api }) => api.orbisMiddleware.model.getModel(modelId)),
            providesTags: [DataTags.GetModel],
        }),
        getModels: build.query({
            queryFn: createQueryFn((_: void, { extra: api }) => api.orbisMiddleware.model.getModels()),
            providesTags: [DataTags.GetModels],
        }),
        getModelHoldingInfo: build.query({
            queryFn: createQueryFn(
                (
                    { modelId, symbol, currency }: { modelId: number; symbol: string; currency?: string },
                    { extra: api },
                ) => api.orbisMiddleware.model.getHoldingInfo(modelId, symbol, currency),
            ),
        }),
        getModelDriftReport: build.query({
            queryFn: createQueryFn(
                ({ modelId, accountIds }: { modelId: number; accountIds?: number[] }, { extra: api }) =>
                    api.orbisMiddleware.model.getDriftReport(modelId, accountIds),
            ),
            providesTags: [DataTags.GetModelDriftReport],
        }),
        getModelsOrders: build.query({
            queryFn: createQueryFn((modelId: number, { extra: api }) =>
                api.orbisMiddleware.model.getOrders(modelId, [
                    OrderFilterType.Latest,
                    OrderFilterType.Open,
                    OrderFilterType.Today,
                ]),
            ),
        }),
        createModel: build.mutation({
            queryFn: createQueryFn(
                async (model: ModelEditFormValues, { extra: api, dispatch }) => {
                    const { data } = await api.orbisSdk.post('v2/advisory/model/create', model);

                    if (model.components.length) {
                        await dispatch(
                            modelApi.endpoints.updateModelComponents.initiate({
                                model: data,
                                newComponents: model.components,
                                oldComponents: [],
                            }),
                        ).unwrap();
                    }

                    dispatch(showToast('Model created'));
                    return data;
                },
                { pendingMessage: 'Creating Model…' },
            ),
            invalidatesTags: [DataTags.GetModels, DataTags.GetOrphanedAccounts],
        }),
        updateModel: build.mutation({
            queryFn: createQueryFn(
                async (
                    { model, currentComponents }: { model: ModelEditFormValues; currentComponents: ModelComponent[] },
                    { extra: api, dispatch },
                ) => {
                    const oldComponents = currentComponents.map((item) => ({
                        symbol: item.symbol,
                        weight: multiply(item.percentage, 100),
                        threshold: 0,
                        companyName: '',
                        color: '',
                    }));

                    await api.orbisSdk.post('v2/advisory/model/update', model);
                    await dispatch(
                        modelApi.endpoints.updateModelComponents.initiate({
                            model,
                            newComponents: model.components,
                            oldComponents,
                        }),
                    ).unwrap();
                    dispatch(showToast('Model updated'));
                },
                { pendingMessage: 'Model is updating…' },
            ),
            invalidatesTags: [DataTags.GetModels, DataTags.GetModel, DataTags.GetOrphanedAccounts],
        }),
        deleteModel: build.mutation({
            queryFn: createQueryFn(
                async (modelId: number, { extra: api, dispatch }) => {
                    const response = await api.orbisSdk.post('v2/advisory/model/delete', { id: modelId });
                    dispatch(showToast('Model deleted'));
                    return response.data;
                },
                { pendingMessage: 'Deleting Model…' },
            ),
            invalidatesTags: [DataTags.GetModels, DataTags.GetOrphanedAccounts],
        }),
        unlinkAccountsToModel: build.mutation({
            queryFn: createQueryFn(
                async ({ modelId, accountIds }: { modelId: number; accountIds: number[] }, { extra: api }) => {
                    await Promise.all(
                        accountIds.map((accountId) =>
                            api.orbisSdk.post('v2/advisory/model/account/unlink', { accountId, modelId }),
                        ),
                    );
                },
                { pendingMessage: 'Updating model accounts…' },
            ),
            invalidatesTags: [DataTags.GetModels, DataTags.GetModel, DataTags.GetOrphanedAccounts],
        }),
        linkAccountsToModel: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        modelId,
                        newAccountIds,
                        oldAccountIds,
                    }: { modelId: number; newAccountIds: number[]; oldAccountIds: number[] },
                    { extra: api },
                ) => {
                    const removedAccountIds = oldAccountIds.filter((oldAccountId) => {
                        if (newAccountIds.length > 0) {
                            return !newAccountIds.includes(oldAccountId);
                        }

                        return true;
                    });

                    const addedAccountIds = newAccountIds.filter(
                        (newAccountId) => !oldAccountIds.includes(newAccountId),
                    );

                    await Promise.all(
                        removedAccountIds
                            .map((accountId) =>
                                api.orbisSdk.post('v2/advisory/model/account/unlink', { accountId, modelId }),
                            )
                            .concat(
                                addedAccountIds.map((accountId) =>
                                    api.orbisSdk.post('v2/advisory/model/account/link', { accountId, modelId }),
                                ),
                            ),
                    );
                },
                { pendingMessage: 'Updating model accounts…' },
            ),
            invalidatesTags: [DataTags.GetModels, DataTags.GetModel, DataTags.GetOrphanedAccounts],
        }),
        getOrphanedAccounts: build.query({
            queryFn: createQueryFn(async (_: void, { extra: api }) => {
                const orphanedAccounts = (await api.orbisSdk.get('v2/advisory/model/accounts/orphaned')).data.map(
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (item: any) => item.account,
                ) as Account[];
                const orphanedAccountIds = orphanedAccounts.map((account) => account.accountId);
                const accounts = await api.orbisMiddleware.branch.getAccounts();
                return accounts.filter((account) => orphanedAccountIds.includes(account.accountId));
            }),
            providesTags: [DataTags.GetOrphanedAccounts],
        }),
    }),
});

export const {
    useGetModelPortfolioQuery,
    useLazyGetModelPortfolioQuery,
    useGetModelAccountTypesBreakdownQuery,
    useGetModelMarketCapBreakdownQuery,
    useGetModelSectorsBreakdownQuery,
    useGetModelAccountSizesBreakdownQuery,
    useGetModelTopPositionsBreakdownQuery,
    useGetModelOverExposedAccountsQuery,
    useGetModelQuery,
    useGetModelsQuery,
    useGetModelHoldingInfoQuery,
    useGetModelDriftReportQuery,
    useGetModelsOrdersQuery,
    useCreateModelMutation,
    useUpdateModelMutation,
    useDeleteModelMutation,
    useLinkAccountsToModelMutation,
    useUnlinkAccountsToModelMutation,
    useGetOrphanedAccountsQuery,
    useLazyGetModelQuery,
} = modelApi;
